import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useImportaTargetareLista } from "@/hooks/api/mutate/useImportaTargetareLista";
import { useCautaTargetareFirma } from "@/hooks/api/query/useCautaTargetareFirma";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type {
  TargetareDimensiune,
  TargetareFirmaCautata,
  TargetareListaFisier,
  TargetareServiciuScurt,
  TargetareTipLista,
} from "@/types/targetare";
import { ETICHETE_TIP_LISTA } from "@/utils/targetareConfig";
import { formatDimensiune } from "@/utils/targetareFormatting";
import { SelectServiciu } from "./CelulaLista";

type AdaugaFirmaListaDialogProps = {
  deschis: boolean;
  onClose: () => void;
  fisiere: TargetareListaFisier[];
  servicii: TargetareServiciuScurt[];
};

const LISTA_NOUA = "__noua__";

export function AdaugaFirmaListaDialog({
  deschis,
  onClose,
  fisiere,
  servicii,
}: AdaugaFirmaListaDialogProps) {
  const { toast } = useToast();
  const importa = useImportaTargetareLista();
  const [q, setQ] = useState("");
  const [qIntarziat, setQIntarziat] = useState("");
  const [firma, setFirma] = useState<TargetareFirmaCautata | null>(null);
  const [lista, setLista] = useState<string>(LISTA_NOUA);
  const [numeNou, setNumeNou] = useState("");
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [tip, setTip] = useState<TargetareTipLista>("NEOFICIALA");
  const cautare = useCautaTargetareFirma(qIntarziat);

  useEffect(() => {
    const t = setTimeout(() => setQIntarziat(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!deschis) return;
    setQ("");
    setQIntarziat("");
    setFirma(null);
    const primaNeoficiala = fisiere.find((f) => f.tip === "NEOFICIALA");
    setLista(primaNeoficiala?.nume_fisier ?? LISTA_NOUA);
    setNumeNou("");
    setServiceId(primaNeoficiala?.service_id ?? null);
    setTip(primaNeoficiala?.tip ?? "NEOFICIALA");
  }, [deschis, fisiere]);

  const ordonate = useMemo(
    () =>
      [...fisiere].sort((a, b) =>
        a.tip === b.tip
          ? a.nume_fisier.localeCompare(b.nume_fisier)
          : a.tip === "NEOFICIALA"
            ? -1
            : 1
      ),
    [fisiere]
  );
  const listaExistenta = fisiere.find((f) => f.nume_fisier === lista) ?? null;
  useEffect(() => {
    if (listaExistenta) {
      setServiceId(listaExistenta.service_id);
      setTip(listaExistenta.tip);
    }
  }, [listaExistenta]);

  const numeFinal = lista === LISTA_NOUA ? numeNou.trim() : lista;
  const dejaPeLista = Boolean(
    firma &&
      listaExistenta?.service_id &&
      firma.servicii_liste?.includes(listaExistenta.service_id)
  );

  const adauga = () => {
    if (!firma || !numeFinal) return;
    importa.mutate(
      {
        nume_fisier: numeFinal,
        randuri: [
          {
            cui: firma.cui,
            denumire: firma.denumire,
            cod_inmatriculare: firma.cod_inmatriculare,
          },
        ],
        service_id: serviceId,
        tip,
        inlocuieste: false,
        final: true,
        sursa: "manual",
      },
      {
        onSuccess: (r) => {
          toast({
            title:
              r.data.inserate > 0
                ? `${firma.denumire} a fost adăugată pe listă`
                : r.data.duplicate > 0
                  ? "Firma era deja pe listă"
                  : "Firma nu a putut fi potrivită",
            description: `Lista „${numeFinal}”. Reîncadrarea aplică schimbarea în tabelul de firme.`,
          });
          setFirma(null);
          setQ("");
        },
        onError: (e) =>
          toast({
            title: "Adăugarea a eșuat",
            description: e.message,
            variant: "destructive",
          }),
      }
    );
  };

  return (
    <Dialog open={deschis} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">
            Adaugă o firmă pe o listă
          </DialogTitle>
          <DialogDescription>
            Caută firma după CUI sau după începutul denumirii (registrul ONRC
            complet), apoi alege lista, de regulă una neoficială.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="CUI sau denumire (minimum 3 caractere)"
            autoFocus
          />
          {cautare.isFetching && (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Se caută…
            </p>
          )}
          {cautare.error && (
            <p className="text-xs text-destructive">{cautare.error.message}</p>
          )}
          {cautare.data && cautare.data.length > 0 && (
            <ul className="max-h-56 space-y-1 overflow-auto rounded border p-1">
              {cautare.data.map((f) => (
                <li key={f.cod_inmatriculare}>
                  <button
                    type="button"
                    onClick={() => setFirma(f)}
                    className={cn(
                      "flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-sm hover:bg-muted",
                      firma?.cod_inmatriculare === f.cod_inmatriculare &&
                        "bg-ds-blue-50"
                    )}
                  >
                    <span className="truncate">
                      {f.denumire}
                      <span className="ml-2 font-mono text-xs text-muted-foreground">
                        {f.cui}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                      {f.judet}
                      {!f.activa && (
                        <Badge variant="destructive" className="font-normal">
                          inactivă
                        </Badge>
                      )}
                      {f.dimensiune_stricta && (
                        <Badge variant="outline" className="font-normal">
                          {formatDimensiune(
                            f.dimensiune_stricta as TargetareDimensiune
                          )}
                        </Badge>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {cautare.data &&
            cautare.data.length === 0 &&
            qIntarziat.length >= 3 &&
            !cautare.isFetching && (
              <p className="text-xs text-muted-foreground">
                Nicio firmă găsită.
              </p>
            )}
          {firma && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              <p className="font-medium">{firma.denumire}</p>
              <p className="text-xs text-muted-foreground">
                CUI {firma.cui} · {firma.cod_inmatriculare} ·{" "}
                {firma.localitate ?? ""} {firma.judet ?? ""}
                {firma.tier && ` · tier ${firma.tier}`}
                {dejaPeLista && " · este deja pe o listă a acestui serviciu"}
              </p>
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1 text-xs">
              <span className="text-muted-foreground">Lista</span>
              <Select value={lista} onValueChange={setLista}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value={LISTA_NOUA}>Listă nouă</SelectItem>
                  {ordonate.map((f) => (
                    <SelectItem key={f.nume_fisier} value={f.nume_fisier}>
                      {ETICHETE_TIP_LISTA[f.tip]} · {f.nume_fisier}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            {lista === LISTA_NOUA && (
              <label className="space-y-1 text-xs">
                <span className="text-muted-foreground">Numele listei noi</span>
                <Input
                  value={numeNou}
                  onChange={(e) => setNumeNou(e.target.value)}
                  className="h-8 text-xs"
                />
              </label>
            )}
            <label className="space-y-1 text-xs">
              <span className="text-muted-foreground">Serviciul NIS2</span>
              <SelectServiciu
                valoare={serviceId}
                servicii={servicii}
                onChange={setServiceId}
                latime="w-full"
                disabled={Boolean(listaExistenta)}
              />
            </label>
            <label className="space-y-1 text-xs">
              <span className="text-muted-foreground">Tipul listei</span>
              <Select
                value={tip}
                onValueChange={(t) => setTip(t as TargetareTipLista)}
                disabled={Boolean(listaExistenta)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(["OFICIALA", "NEOFICIALA"] as TargetareTipLista[]).map(
                    (t) => (
                      <SelectItem key={t} value={t}>
                        {ETICHETE_TIP_LISTA[t]}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Închide
          </Button>
          <Button
            onClick={adauga}
            disabled={!firma || !numeFinal || importa.isPending}
          >
            {importa.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            Adaugă pe listă
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
